import React from 'react';
import { STATUS_CONFIG } from '../constants';
import { RecommendationStatus } from '../types';

type StampSize = 'sm' | 'md';

interface StatusStampProps {
  status: RecommendationStatus;
  /** `sm` for list rows and filter chips, `md` for the drawer. */
  size?: StampSize;
  /** Slight tilt, like it was pressed by hand. */
  rotate?: boolean;
  /** Play the "pressed on" entrance animation. */
  animate?: boolean;
  /** Solid status fill — used for selected filter / picker chips. */
  filled?: boolean;
  /** Override the uppercase stamp copy (e.g. the friendlier sentence-case label). */
  label?: string;
  /** Wrap the label at this width instead of the size default (tight list rows). */
  labelMaxWidth?: string;
  className?: string;
}

const SIZES: Record<StampSize, {
  box: string;
  text: string;
  icon: number;
  border: string;
  /** Wrap point for the label. `undefined` keeps it on one line. */
  maxLabel?: string;
}> = {
  sm: {
    box: 'px-1.5 py-[2px] gap-1',
    text: 'text-[8px] tracking-[0.05em]',
    icon: 10,
    border: '1.5px',
  },
  md: {
    box: 'px-2 py-1 gap-1.5',
    text: 'text-[10px] tracking-[0.07em]',
    icon: 13,
    border: '2px',
    // Wide enough for RECOMMENDED on one line; longer labels wrap to two.
    maxLabel: '100px',
  },
};

/**
 * Rubber-stamp status badge.
 *
 * Built from the status token colour: a double rule (2px outer border plus a
 * hairline inner rule drawn with inset shadows), a faint tint of the same
 * colour behind it, ink-coloured uppercase display type and the lucide glyph.
 * `multiply` blending and a touch of transparency give it the slightly
 * uneven, inked-on look rather than a flat pill.
 */
export const StatusStamp: React.FC<StatusStampProps> = ({
  status,
  size = 'md',
  rotate = true,
  animate = false,
  filled = false,
  label,
  labelMaxWidth,
  className = '',
}) => {
  const config = STATUS_CONFIG[status];
  const { Icon } = config;
  const s = SIZES[size];
  const maxWidth = labelMaxWidth ?? s.maxLabel;

  const colour = `var(${config.cssVar})`;
  const ink = `var(${config.cssVar}-ink)`;

  // White text on the yellow seal is only 1.85:1, so that one status keeps its
  // ink colour even when filled. Every other status has enough contrast for white.
  const filledInk = status === RecommendationStatus.SEAL_OF_APPROVAL ? ink : 'white';

  const tint = filled ? colour : `color-mix(in srgb, ${colour} 10%, white)`;
  const rule = filled ? 'rgba(255,255,255,0.55)' : `color-mix(in srgb, ${colour} 45%, transparent)`;

  const style: React.CSSProperties = {
    // Consumed by the animate-stamp-press keyframes so the tilt survives the animation.
    ['--stamp-rotate' as string]: rotate ? '-3deg' : '0deg',
    transform: animate ? undefined : `rotate(${rotate ? '-3deg' : '0deg'})`,
    color: filled ? filledInk : ink,
    backgroundColor: tint,
    border: `${s.border} solid ${filled ? colour : ink}`,
    // Hairline inner rule, 2px in from the border — the classic stamp double line.
    boxShadow: `inset 0 0 0 2px ${tint}, inset 0 0 0 3px ${rule}`,
    mixBlendMode: filled ? 'normal' : 'multiply',
    opacity: filled ? 1 : 0.94,
  };

  return (
    <span
      title={config.label}
      style={style}
      className={`
        inline-flex items-center shrink-0 select-none rounded-[3px]
        ${s.box} ${animate ? 'animate-stamp-press' : ''} ${className}
      `}
    >
      <Icon size={s.icon} strokeWidth={2.75} className="shrink-0" />
      <span
        className={`font-display font-extrabold uppercase leading-[1.05] ${s.text} ${maxWidth ? 'text-center' : 'whitespace-nowrap'}`}
        style={maxWidth ? { maxWidth } : undefined}
      >
        {label ?? config.label}
      </span>
    </span>
  );
};
